import React, { useState, useEffect } from 'react';
import { Send, Check, Loader2, MessageSquare, Settings, X, ShieldCheck } from 'lucide-react';

interface TelegramExportButtonProps {
  title: string;
  content: string;
  agentName?: string;
  agentId?: string;
  className?: string;
  variant?: 'primary' | 'secondary' | 'compact' | 'emerald';
  isUk?: boolean;
}

export default function TelegramExportButton({
  title,
  content,
  agentName = 'Deep Research Hub',
  agentId,
  className = '',
  variant = 'secondary',
  isUk = true
}: TelegramExportButtonProps) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [customChatId, setCustomChatId] = useState('');
  const [effectiveChatId, setEffectiveChatId] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Load user telegram settings on mount
  useEffect(() => {
    fetch('/api/user/telegram-settings')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          if (data.telegram_chat_id) setCustomChatId(data.telegram_chat_id);
          if (data.effective_chat_id) setEffectiveChatId(data.effective_chat_id);
        }
      })
      .catch(() => {});
  }, []);

  const handleSendToTelegram = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (loading) return;

    setLoading(true);
    setStatusMessage(null);

    try {
      const res = await fetch('/api/send-report-telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          content,
          agentName,
          agentId,
          customChatId: customChatId.trim() || undefined,
          sendDocx: true
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSuccess(true);
        setStatusMessage(isUk ? '✅ Звіт та Word (.docx) надіслано в Telegram!' : '✅ Sent to Telegram with Word (.docx)!');
        if (data.targetChatId) setEffectiveChatId(data.targetChatId);
        setTimeout(() => {
          setSuccess(false);
          setStatusMessage(null);
        }, 4000);
        if (showSettingsModal) setShowSettingsModal(false);
      } else {
        alert(data.error || (isUk ? 'Не вдалося надіслати в Telegram' : 'Failed to send to Telegram'));
      }
    } catch (err: any) {
      alert(isUk ? 'Помилка мережі при відправці в Telegram' : 'Network error sending to Telegram');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChatId = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/user/telegram-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegram_chat_id: customChatId.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setEffectiveChatId(customChatId.trim());
        await handleSendToTelegram();
      }
    } catch (err) {
      alert(isUk ? 'Помилка збереження Chat ID' : 'Error saving Chat ID');
    }
  };

  // Base styling variants
  let buttonClasses = 'px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95 select-none ';

  if (variant === 'primary') {
    buttonClasses += 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white shadow-blue-500/20';
  } else if (variant === 'emerald') {
    buttonClasses += 'bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white shadow-emerald-500/20';
  } else if (variant === 'compact') {
    buttonClasses = 'px-2.5 py-1 text-[11px] font-bold uppercase rounded-md bg-sky-950/80 hover:bg-sky-900 text-sky-300 border border-sky-700/50 flex items-center gap-1 cursor-pointer transition-colors';
  } else {
    buttonClasses += 'bg-slate-800 hover:bg-slate-700 text-sky-300 border border-slate-700 hover:border-sky-500/50';
  }

  return (
    <div className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={handleSendToTelegram}
        disabled={loading}
        title={isUk ? 'Надіслати офіційний звіт та Word (.docx) у ваш Telegram' : 'Send report and Word (.docx) to your Telegram'}
        className={`${buttonClasses} ${className} ${loading ? 'opacity-70 cursor-wait' : ''}`}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-400" />
        ) : success ? (
          <Check className="w-3.5 h-3.5 text-emerald-400 animate-bounce" />
        ) : (
          <Send className="w-3.5 h-3.5 text-sky-400" />
        )}
        <span className="font-semibold tracking-wide">
          {loading 
            ? (isUk ? 'Відправка...' : 'Sending...') 
            : success 
            ? (isUk ? 'Надіслано!' : 'Sent!') 
            : (isUk ? 'У Telegram (1-клік)' : 'To Telegram')}
        </span>
      </button>

      {/* Quick settings gear for Chat ID */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShowSettingsModal(true);
        }}
        title={isUk ? 'Налаштувати Telegram Chat ID' : 'Configure Telegram Chat ID'}
        className="p-1.5 text-slate-400 hover:text-sky-400 hover:bg-slate-800 rounded-lg border border-slate-800 transition-colors cursor-pointer"
      >
        <Settings className="w-3 h-3" />
      </button>

      {/* Inline status tooltip / badge */}
      {statusMessage && (
        <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-slate-900 border border-emerald-500/80 text-emerald-300 text-[11px] font-bold py-1 px-2.5 rounded-md shadow-xl whitespace-nowrap z-50 animate-fade-in flex items-center gap-1">
          <Check className="w-3 h-3 text-emerald-400 shrink-0" />
          <span>{statusMessage}</span>
        </div>
      )}

      {/* Settings & Destination Modal */}
      {showSettingsModal && (
        <div 
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs z-50 flex items-center justify-center p-4"
          onClick={() => setShowSettingsModal(false)}
        >
          <div 
            className="bg-slate-900 border border-sky-500/40 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4 text-left animate-fade-in"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-sky-500/10 text-sky-400 rounded-xl border border-sky-500/20">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    {isUk ? 'Налаштування Telegram Доставки' : 'Telegram Delivery Settings'}
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    {isUk ? 'Миттєве отримання звітів та файлів Word (.docx)' : 'Instant report & Word (.docx) sync'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowSettingsModal(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveChatId} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  {isUk ? 'Ваш Telegram Chat ID (особистий чи групи):' : 'Your Telegram Chat ID:'}
                </label>
                <input
                  type="text"
                  placeholder="наприклад: 1421638496 або @my_channel"
                  value={customChatId}
                  onChange={e => setCustomChatId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-white rounded-xl px-3.5 py-2.5 text-xs outline-none transition-all placeholder:text-slate-600 font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">
                  💡 {isUk 
                    ? 'За замовчуванням звіти доставляються у підключений бот платформи. Щоб дізнатися свій особистий Chat ID, напишіть боту @userinfobot у Telegram.' 
                    : 'To find your Chat ID, message @userinfobot in Telegram.'}
                </p>
              </div>

              {effectiveChatId && (
                <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 flex items-center gap-2 text-xs text-slate-300">
                  <ShieldCheck className="w-4 h-4 text-sky-400 shrink-0" />
                  <span>
                    {isUk ? 'Поточний активний Chat ID:' : 'Active Chat ID:'}{' '}
                    <strong className="text-white font-mono">{effectiveChatId}</strong>
                  </span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowSettingsModal(false)}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition-colors cursor-pointer"
                >
                  {isUk ? 'Скасувати' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer shadow-lg shadow-sky-600/30 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  <span>{isUk ? 'Зберегти & Надіслати зараз' : 'Save & Send Now'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
